import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtom, useAtomValue } from 'jotai';
import { useInView } from 'react-intersection-observer';

import { usePreferences } from '@/react-query/usePreferences';
import { useVoiceNoteTemplates } from '@/react-query/videa-notes/useVoiceNoteTemplates';

import {
  ambientViewTypeAtom,
  jumpToCurrentApptAtom,
  searchAtom,
  selectDateAtom,
} from '../patientList/atoms';
import { PatientListPageLayout } from '../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../patientList/types';
import { getNextAppointmentByStandalonePatientVisitId } from '../patientList/useScrollAppointmentIntoView';

import { AmbientTable } from './AmbientTable';
import { AmbientViewTabs } from './AmbientViewTabs';
import {
  useAmbientAppointmentsData,
  useAmbientInfiniteScroll,
  useAmbientSorting,
} from './hooks';
import { TemplatesTable } from './TemplatesTable';

export const AmbientPage = () => {
  const [selectedDate, setSelectedDate] = useAtom(selectDateAtom);
  const search = useAtomValue(searchAtom);
  const ambientViewType = useAtomValue(ambientViewTypeAtom);
  const { inView: isNextAppointmentInView } = useInView();
  const nextAppointmentRef = useRef<HTMLTableRowElement>(null);
  const jumpToCurrentAppt = useAtomValue(jumpToCurrentApptAtom);
  const { data: preferences } = usePreferences();

  // Ambient-specific hooks (for Notes view)
  const {
    appointmentsWithTreatmentsAndNotes,
    isLoading,
    isError,
    hasNextPage,
    isAnyBatchLoading,
    fetchNextPage,
    invalidateNotesCache,
  } = useAmbientAppointmentsData();

  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useAmbientSorting(appointmentsWithTreatmentsAndNotes);

  // Templates-specific hooks (for Templates view)
  const {
    data: templatesData = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useVoiceNoteTemplates();

  // Templates sorting state
  const [templatesSortField, setTemplatesSortField] = useState('name');
  const [templatesSortDirection, setTemplatesSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  const sortedTemplates = useMemo(() => {
    const sorted = [...templatesData];
    sorted.sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      switch (templatesSortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'creator':
          aVal = a.createdBy?.name || '';
          bVal = b.createdBy?.name || '';
          break;
        default:
          return 0;
      }

      const comparison = aVal.localeCompare(bVal);
      return templatesSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [templatesData, templatesSortField, templatesSortDirection]);

  const handleTemplatesSort = (field: string, direction: 'asc' | 'desc') => {
    setTemplatesSortField(field);
    setTemplatesSortDirection(direction);
  };

  const nextAppointmentStandalonePatientVisitId =
    getNextAppointmentByStandalonePatientVisitId({
      appointments: sortedAppointments,
      selectedDate,
    });

  // Calculate indices using useMemo
  const { nextAppointmentIndex } = useMemo(() => {
    return {
      nextAppointmentIndex: sortedAppointments.findIndex(
        (appointment) =>
          appointment.appointment.standalonePatientVisitId ===
          nextAppointmentStandalonePatientVisitId,
      ),
    };
  }, [sortedAppointments, nextAppointmentStandalonePatientVisitId]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref: sentinelRef } = useAmbientInfiniteScroll(
    hasNextPage,
    isLoading,
    fetchNextPage,
    scrollContainerRef,
  );

  const scrollNextAppointmentIntoView = useCallback(() => {
    if (nextAppointmentIndex >= 0) {
      nextAppointmentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [nextAppointmentIndex, nextAppointmentRef]);

  // Set today as default date and reset when search is cleared
  useEffect(() => {
    if (search === '' && !selectedDate) {
      setSelectedDate(new Date());
    }
  }, [search, selectedDate, setSelectedDate]);

  useEffect(() => {
    if (jumpToCurrentAppt === 'true') {
      scrollNextAppointmentIntoView();
    }
  }, [jumpToCurrentAppt, scrollNextAppointmentIntoView]);

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-screen vai-overflow-hidden">
      <AmbientViewTabs />

      {ambientViewType === 'templates' &&
      preferences?.enableVoiceNotesCustomTemplates ? (
        <div className="vai-flex-1 vai-flex vai-flex-col vai-bg-gray-100 vai-overflow-hidden">
          <div className="vai-flex vai-flex-col vai-h-full">
            {isTemplatesError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading templates</Trans>
                </div>
              </div>
            ) : isLoadingTemplates ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-gray-600">
                  <Trans>Loading templates...</Trans>
                </div>
              </div>
            ) : (
              <TemplatesTable
                templates={sortedTemplates}
                onSort={handleTemplatesSort}
                sortField={templatesSortField}
                sortDirection={templatesSortDirection}
                scrollContainerRef={scrollContainerRef}
                sentinelRef={sentinelRef}
              />
            )}
          </div>
        </div>
      ) : (
        // Notes View - default
        <PatientListPageLayout
          context={PatientListPageLayoutContextMap.Ambient}
          allAppointments={sortedAppointments}
          isLoadingAppointments={isLoading || isAnyBatchLoading}
          isLoadingVisitSummaries={false}
          nextAppointmentIndex={nextAppointmentIndex}
          isNextAppointmentInView={isNextAppointmentInView}
          onScrollNextAppointmentIntoView={scrollNextAppointmentIntoView}
        >
          <div className="vai-flex vai-flex-col vai-h-[87vh]">
            {isError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading appointments</Trans>
                </div>
              </div>
            ) : (
              <AmbientTable
                appointments={sortedAppointments}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                invalidateNotesCache={invalidateNotesCache}
                scrollContainerRef={scrollContainerRef}
                sentinelRef={sentinelRef}
                nextAppointmentRef={nextAppointmentRef}
              />
            )}
          </div>
        </PatientListPageLayout>
      )}
    </div>
  );
};
